import type {
  ApplicationStudioArtifactKind,
  ApplicationStudioArtifactSummary,
} from '@tabbio/types';

export type StudioTextSegment = {
  text: string;
  strong?: boolean;
  code?: boolean;
};

const COPY_KEYS_BY_KIND: Partial<Record<ApplicationStudioArtifactKind, string[]>> = {
  cv_fit_report: [
    'overallScore',
    'gccFitScore',
    'atsScore',
    'summary',
    'strengths',
    'risks',
    'missingSkills',
    'missingTools',
    'recommendedCertifications',
    'rewriteSuggestions',
    'nextActions',
  ],
  cover_letter: ['body', 'highlights', 'tone'],
  interview_prep: [
    'overview',
    'questions',
    'practicePlan',
    'bilingualTips',
    'feedbackRubric',
  ],
  salary_report: [
    'country',
    'currency',
    'low',
    'median',
    'high',
    'confidence',
    'seniorityWarning',
    'negotiationScript',
    'marketNotes',
  ],
  recruiter_preview: [
    'headline',
    'profileScore',
    'recruiterSummary',
    'visibleStrengths',
    'concerns',
    'missingEvidence',
    'verificationSignals',
    'rankingRisks',
    'nextActions',
  ],
  screening_answers: ['answers'],
  apply_checklist: ['ready', 'steps', 'blockers', 'approvalRequired'],
  application_packet: ['ready', 'steps', 'blockers', 'approvalRequired'],
};

const SKIP_COPY_KEYS = new Set(['dataSource', 'sourceVersion', 'id']);

export function cleanStudioMarkdownText(value: string) {
  let text = value.replace(/\r\n/g, '\n');

  text = text.replace(/```[\s\S]*?```/g, (match) =>
    match.replace(/^```\w*\n?/, '').replace(/\n?```$/, ''),
  );
  text = text.replace(/^\s{0,3}#{1,6}\s+/gm, '');
  text = text.replace(/^\s{0,3}>\s?/gm, '');
  text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  text = text.replace(/`([^`]+)`/g, '$1');
  text = text.replace(/\*\*([^*]+)\*\*/g, '$1');
  text = text.replace(/__([^_]+)__/g, '$1');
  text = text.replace(/(^|[\s(])\*([^*\n]+)\*(?=[\s).,!?;:]|$)/g, '$1$2');
  text = text.replace(/(^|[\s(])_([^_\n]+)_(?=[\s).,!?;:]|$)/g, '$1$2');
  text = text.replace(/^\s{0,3}[-*+]\s+/gm, '- ');

  return text.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

export function studioMarkdownSegments(value: string): StudioTextSegment[] {
  const prepared = value
    .replace(/\r\n/g, '\n')
    .replace(/```[\s\S]*?```/g, (match) =>
      match.replace(/^```\w*\n?/, '').replace(/\n?```$/, ''),
    )
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s{0,3}>\s?/gm, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/(^|[\s(])\*([^*\n]+)\*(?=[\s).,!?;:]|$)/g, '$1$2')
    .replace(/(^|[\s(])_([^_\n]+)_(?=[\s).,!?;:]|$)/g, '$1$2');
  const segments: StudioTextSegment[] = [];
  const pattern = /(\*\*([^*]+)\*\*|__([^_]+)__|`([^`]+)`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(prepared)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ text: prepared.slice(lastIndex, match.index) });
    }
    segments.push({
      text: match[2] ?? match[3] ?? match[4] ?? '',
      strong: Boolean(match[2] ?? match[3]),
      code: Boolean(match[4]),
    });
    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < prepared.length) {
    segments.push({ text: prepared.slice(lastIndex) });
  }

  return segments.length > 0 ? segments : [{ text: cleanStudioMarkdownText(value) }];
}

export function applicationStudioArtifactCopyText(
  artifact: ApplicationStudioArtifactSummary,
  fallbackTitle: string,
) {
  const content = artifact.content && typeof artifact.content === 'object' ? artifact.content : {};
  const lines = [artifact.title ?? fallbackTitle];
  const orderedKeys = COPY_KEYS_BY_KIND[artifact.kind] ?? [];
  const usedKeys = new Set<string>();

  for (const key of orderedKeys) {
    usedKeys.add(key);
    lines.push(...copyLinesForValue(key, content[key]));
  }

  for (const [key, value] of Object.entries(content)) {
    if (usedKeys.has(key) || SKIP_COPY_KEYS.has(key)) continue;
    lines.push(...copyLinesForValue(key, value));
  }

  if (lines.length === 1 && artifact.plaintext) {
    lines.push(artifact.plaintext);
  }

  return cleanStudioMarkdownText(
    lines
      .filter((line) => line.trim().length > 0)
      .join('\n\n'),
  );
}

function copyLinesForValue(key: string, value: unknown): string[] {
  if (SKIP_COPY_KEYS.has(key) || value === null || value === undefined || value === '') {
    return [];
  }

  const label = humanizeKey(key);
  if (typeof value === 'string') {
    return [`${label}: ${value}`];
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return [`${label}: ${String(value)}`];
  }
  if (Array.isArray(value)) {
    const items = value.flatMap((item, index) => copyLinesForArrayItem(item, index));
    return items.length > 0 ? [`${label}:`, ...items] : [];
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).flatMap(
      ([childKey, childValue]) => copyLinesForValue(childKey, childValue),
    );
    return entries.length > 0 ? [`${label}:`, ...entries] : [];
  }

  return [];
}

function copyLinesForArrayItem(value: unknown, index: number): string[] {
  if (value === null || value === undefined || value === '') {
    return [];
  }
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return [`- ${String(value)}`];
  }
  if (Array.isArray(value)) {
    return value.flatMap((item, itemIndex) => copyLinesForArrayItem(item, itemIndex));
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).flatMap(
      ([childKey, childValue]) => copyLinesForValue(childKey, childValue),
    );
    return entries.length > 0 ? [`${index + 1}.`, ...entries] : [];
  }
  return [];
}

function humanizeKey(key: string) {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
